import { useMutation } from '@apollo/client';
import type { InsertObligationOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertObligationOversightResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertObligationOversightResultInput } from './useInsertObligationOversightResultTRPC';
import { useInsertObligationOversightResultTRPC } from './useInsertObligationOversightResultTRPC';

export const useInsertObligationOversightResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertObligationOversightResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'obligation_oversight_result');
        evictField(cache, 'oversight_assessment');
        evictField(cache, 'obligation_oversight_result_aggregate');
      },
    }
  );

  const trpcMutation = useInsertObligationOversightResultTRPC();

  const insertObligationOversightResult = async (
    variables: InsertObligationOversightResultInput
  ): Promise<InsertObligationOversightResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertObligationOversightResult(variables);
    }

    const result = await insertGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to insert obligation oversight result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertObligationOversightResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertObligationOversightResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
